import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Film } from "@/types/film";

interface HeroCarouselProps {
  films: Film[];
}

export default function HeroCarousel({ films }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const total = films.length;

  const goTo = useCallback(
    (next: number) => {
      setIndex(((next % total) + total) % total);
    },
    [total],
  );

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % total);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [paused, total]);

  const active = films[index];

  return (
    <section
      className="relative h-[600px] w-full overflow-hidden bg-primary-950 md:h-[700px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(event) => {
        touchStartX.current = event.touches[0].clientX;
      }}
      onTouchEnd={(event) => {
        if (touchStartX.current === null) return;
        const delta = event.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > 50) {
          goTo(index + (delta < 0 ? 1 : -1));
        }
        touchStartX.current = null;
      }}
      aria-roledescription="轮播图"
      aria-label="精选伦理影视推荐"
    >
      {films.map((film, slideIndex) => (
        <div
          key={film.id}
          className={`absolute inset-0 transition-opacity duration-[900ms] ease-out ${
            slideIndex === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={film.backdrop}
            alt={`${film.title}剧照`}
            title={`${film.title} - 伦理视频精选`}
            className={`h-full w-full object-cover object-center ${
              slideIndex === index ? "animate-slow-zoom" : ""
            }`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/70 to-primary-950/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-transparent to-primary-950/40" />
        </div>
      ))}

      <div className="relative z-10 flex h-full w-full items-end px-4 pb-20 md:items-center md:px-10 md:pb-0">
        <div className="w-full max-w-3xl md:pt-20">
          <div key={`meta-${active.id}`} className="animate-fade-up">
            <div className="flex flex-wrap items-center gap-2 text-xs text-background-100">
              <span className="flex items-center gap-1 rounded-full bg-accent-500 px-2.5 py-1 font-medium text-background-50">
                <i className="ri-fire-line" />
                本季精选
              </span>
              <span className="rounded-full border border-background-50/30 px-2.5 py-1">
                {active.kind === "movie" ? "伦理电影" : "伦理剧集"}
              </span>
              <span className="text-background-200">
                {active.year} · {active.region} · {active.genres.join(" / ")}
              </span>
            </div>

            <h1 className="mt-5 font-heading text-3xl font-semibold leading-tight text-background-50 sm:text-4xl md:text-5xl lg:text-6xl">
              {active.title}
            </h1>
            <p className="mt-3 font-heading text-sm italic tracking-wide text-accent-300 md:text-base">
              {active.tagline}
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-background-100">
              <span className="flex items-center gap-1.5 text-accent-400">
                <i className="ri-star-fill" />
                <strong className="text-base font-semibold text-accent-300">{active.rating.toFixed(1)}</strong>
                <span className="text-xs text-background-300">/ 10</span>
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-user-star-line text-background-300" />
                导演 {active.director}
              </span>
              <span className="hidden items-center gap-1.5 sm:flex">
                <i className="ri-time-line text-background-300" />
                {active.duration}
              </span>
            </div>

            <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-200 line-clamp-3 md:text-base">
              {active.summary}
            </p>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to={`/detail/${active.id}`}
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 cursor-pointer"
              >
                <i className="ri-play-circle-line text-base" />
                查看作品详情
              </Link>
              <Link
                to={active.kind === "movie" ? "/movies" : "/series"}
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-6 py-3 text-sm text-background-50 transition-colors hover:border-background-50 hover:bg-background-50/10 cursor-pointer"
              >
                浏览全部{active.kind === "movie" ? "电影" : "剧集"}
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-4 z-20 flex items-center gap-4 md:right-10 md:bottom-10">
        <div className="hidden items-center gap-2 sm:flex">
          {films.map((film, dotIndex) => (
            <button
              key={film.id}
              type="button"
              aria-label={`切换到${film.title}`}
              onClick={() => goTo(dotIndex)}
              className={`h-1 rounded-full transition-all duration-300 cursor-pointer ${
                dotIndex === index ? "w-8 bg-accent-500" : "w-4 bg-background-50/40 hover:bg-background-50/70"
              }`}
            />
          ))}
        </div>
        <span className="font-label text-xs tracking-[0.2em] text-background-200">
          {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="上一张"
            onClick={() => goTo(index - 1)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/35 text-background-50 transition-colors hover:bg-background-50/15 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line" />
          </button>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => goTo(index + 1)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/35 text-background-50 transition-colors hover:bg-background-50/15 cursor-pointer"
          >
            <i className="ri-arrow-right-s-line" />
          </button>
        </div>
      </div>
    </section>
  );
}